import React, { useState } from "react";
import toast from 'react-hot-toast';
import {
  crearVacuna,
  actualizarVacuna,
  eliminarVacuna,
  actualizarDesparasitacion,
  eliminarDesparasitacion,
  crearDesparasitacion,
  crearRecordatorioPersonal
} from "../../services/api";

export default function ProximasSection({
  cartillaData,
  objetoId,
  mascotaInfo,
  isVetAccess,
  proximasConRecordatorio,
  setProximasConRecordatorio,
  creandoRecordatorio,
  setCreandoRecordatorio,
  setVacunas,
  setDesparasitaciones
}) {
  const [proximasSectionOpen, setProximasSectionOpen] = useState(false);
  const [showAddProximaModal, setShowAddProximaModal] = useState(false);
  const [showConfirmarAplicacionModal, setShowConfirmarAplicacionModal] = useState(false);
  const [proximaParaConfirmar, setProximaParaConfirmar] = useState(null);
  const [savingProxima, setSavingProxima] = useState(false);

  function getProximaReminderKey(proxima) {
    const nombreItem = proxima.tipo === 'vacuna'
      ? `Vacuna: ${proxima.nombre}`
      : `Desparasitación: ${proxima.nombre}`;
    return `${nombreItem.slice(0, 100)}|${proxima.fechaProgramada}`;
  }

  function tieneRecordatorio(proxima) {
    const key = getProximaReminderKey(proxima);
    return proximasConRecordatorio[proxima.id] || proximasConRecordatorio[key];
  }

  async function handleCrearRecordatorioProxima(proxima) {
    if (tieneRecordatorio(proxima)) {
      toast('Ya se crearon recordatorios para este ítem');
      return;
    }

    setCreandoRecordatorio(prev => ({ ...prev, [proxima.id]: true }));
    try {
      const nombreItem = proxima.tipo === 'vacuna'
        ? `Vacuna: ${proxima.nombre}`
        : `Desparasitación: ${proxima.nombre}`;
      const queText = nombreItem.slice(0, 100);

      const resp = await crearRecordatorioPersonal(objetoId, {
        que: queText,
        fecha: proxima.fechaProgramada,
        hora: '10:00',
        repetir: 'no',
        objetoNombre: mascotaInfo?.nombreMascota || mascotaInfo?.nombre || 'Mascota',
        objetoTipo: 'mascota',
        tipoDocumento: 'salud',
      });

      if (!resp.error) {
        const key = `${queText}|${proxima.fechaProgramada}`;
        setProximasConRecordatorio(prev => ({ ...prev, [proxima.id]: true, [key]: true }));
        toast.success('Recordatorios creados (1 día antes y 2 horas antes)');
      } else {
        console.error('[handleCrearRecordatorioProxima] 400/error:', resp.error, { proxima });
        if (resp.error?.includes('Ya existe')) {
          const key = `${queText}|${proxima.fechaProgramada}`;
          setProximasConRecordatorio(prev => ({ ...prev, [proxima.id]: true, [key]: true }));
          toast('Ya existía un recordatorio para este ítem');
        } else {
          toast.error(resp.error);
        }
      }
    } catch (error) {
      console.error('[handleCrearRecordatorioProxima] Error:', error);
      toast.error('Error al crear recordatorios');
    } finally {
      setCreandoRecordatorio(prev => ({ ...prev, [proxima.id]: false }));
    }
  }

  async function handleAddProxima(data) {
    setSavingProxima(true);
    try {
      if (data.tipoProxima === 'vacuna') {
        const resp = await crearVacuna(objetoId, {
          nombre: data.nombre,
          fecha: null,
          estado: 'programada',
          fechaProgramada: data.fechaProgramada,
          origenImportacion: 'manual',
        });
        if (!resp.error && resp.data) {
          setVacunas(prev => [resp.data, ...prev]);
          toast.success('Vacuna programada agregada');
        } else {
          toast.error(resp.error || 'Error al crear vacuna programada');
        }
      } else {
        let tipoDesp = null;
        try {
          const catalogo = (await import('../../data/catalogoMedicamentos.json')).default;
          const match = catalogo.desparasitantes.find(
            d => d.nombre.toLowerCase() === data.nombre.toLowerCase()
          );
          if (match) tipoDesp = match.tipo;
        } catch (e) { /* ignore */ }

        const resp = await crearDesparasitacion(objetoId, {
          producto: data.nombre,
          fecha: null,
          estado: 'programada',
          fechaProgramada: data.fechaProgramada,
          tipoDesparasitacion: tipoDesp,
          origenImportacion: 'manual',
        });
        if (!resp.error && resp.data) {
          setDesparasitaciones(prev => [resp.data, ...prev]);
          toast.success('Desparasitación programada agregada');
        } else {
          toast.error(resp.error || 'Error al crear desparasitación programada');
        }
      }
    } catch (error) {
      console.error('[handleAddProxima] Error:', error);
      toast.error('Error al agregar próxima');
    }
    setSavingProxima(false);
    setShowAddProximaModal(false);
  }

  async function handleConfirmarAplicacion(proxima, datosAplicacion) {
    setSavingProxima(true);
    try {
      if (proxima.tipo === 'vacuna') {
        if (proxima.estado === 'programada') {
          const updates = {
            estado: 'aplicada',
            fecha: datosAplicacion.fecha,
            fechaProgramada: null,
            laboratorio: datosAplicacion.laboratorio || null,
            lote: datosAplicacion.lote || null,
          };
          const resp = await actualizarVacuna(objetoId, proxima.id, updates);
          if (!resp.error) {
            setVacunas(prev => prev.map(v => v.id === proxima.id ? { ...v, ...updates } : v));
            toast.success('Vacuna confirmada como aplicada');
          } else {
            toast.error(resp.error);
          }
        }
      } else {
        if (proxima.estado === 'programada') {
          let tipoDesp = proxima.origen?.tipoDesparasitacion || null;
          if (!tipoDesp) {
            try {
              const catalogo = (await import('../../data/catalogoMedicamentos.json')).default;
              const match = catalogo.desparasitantes.find(
                d => d.nombre.toLowerCase() === proxima.nombre.toLowerCase()
              );
              if (match) tipoDesp = match.tipo;
            } catch (e) { /* ignore */ }
          }

          const updates = {
            estado: 'aplicada',
            fecha: datosAplicacion.fecha,
            fechaProgramada: null,
            tipoDesparasitacion: tipoDesp,
            aplicadoEn: datosAplicacion.aplicadoEn || null,
            lote: datosAplicacion.lote || null,
          };
          const resp = await actualizarDesparasitacion(objetoId, proxima.id, updates);
          if (!resp.error) {
            setDesparasitaciones(prev => prev.map(d => d.id === proxima.id ? { ...d, ...updates } : d));
            toast.success('Desparasitación confirmada como aplicada');
          } else {
            toast.error(resp.error);
          }
        }
      }
    } catch (error) {
      console.error('[handleConfirmarAplicacion] Error:', error);
      toast.error('Error al confirmar aplicación');
    }
    setSavingProxima(false);
    setShowConfirmarAplicacionModal(false);
    setProximaParaConfirmar(null);
  }

  async function handleDeleteProxima(proxima) {
    const label = proxima.tipo === 'vacuna' ? 'vacuna programada' : 'desparasitación programada';
    if (!window.confirm(`¿Eliminar esta ${label}?`)) return;

    try {
      if (proxima.tipo === 'vacuna' && proxima.estado === 'programada') {
        const resp = await eliminarVacuna(objetoId, proxima.id);
        if (!resp.error) {
          setVacunas(prev => prev.filter(v => v.id !== proxima.id));
          toast.success('Vacuna programada eliminada');
        } else {
          toast.error(resp.error);
        }
      } else if (proxima.tipo === 'desparasitacion' && proxima.estado === 'programada') {
        const resp = await eliminarDesparasitacion(objetoId, proxima.id);
        if (!resp.error) {
          setDesparasitaciones(prev => prev.filter(d => d.id !== proxima.id));
          toast.success('Desparasitación programada eliminada');
        } else {
          toast.error(resp.error);
        }
      }
    } catch (error) {
      console.error('[handleDeleteProxima] Error:', error);
      toast.error('Error al eliminar');
    }
  }

  return (
    <>
      <div style={{
        marginBottom: 'var(--space-3)',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        <div
          onClick={() => setProximasSectionOpen(!proximasSectionOpen)}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 12px',
            backgroundColor: '#f9fafb',
            cursor: 'pointer',
            userSelect: 'none'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i
              className={`bi bi-chevron-${proximasSectionOpen ? 'down' : 'right'}`}
              style={{ fontSize: '12px', color: 'var(--gray-500)', transition: 'transform 0.2s' }}
            ></i>
            <span style={{ fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-sm)', color: 'var(--gray-700)' }}>
              Pr&oacute;ximas{cartillaData.proximas.length > 0 ? ` (${cartillaData.proximas.length})` : ''}
            </span>
            {cartillaData.proximas.some(p => p.vencida) && (
              <span style={{
                backgroundColor: '#fef2f2',
                color: '#dc2626',
                fontSize: '9px',
                padding: '1px 6px',
                borderRadius: '4px',
                fontWeight: '600'
              }}>
                Atrasadas
              </span>
            )}
          </div>
          <div onClick={(e) => e.stopPropagation()}>
            <button
              className="btn btn-sm"
              onClick={() => setShowAddProximaModal(true)}
              title="Programar vacuna o desparasitaci&oacute;n"
              style={{
                fontSize: '11px',
                padding: '4px 10px',
                backgroundColor: '#fff',
                color: '#2563eb',
                border: '1.5px solid #2563eb',
                borderRadius: '6px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#eff6ff'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#fff'}
            >
              <i className="bi bi-plus me-1"></i>
              Agregar
            </button>
          </div>
        </div>
        {proximasSectionOpen && (
        <div style={{ padding: '10px 12px' }}>
        {cartillaData.proximas.length > 0 ? (
          <div style={{ fontSize: 'var(--font-size-xs)' }}>
            {cartillaData.proximas.map((p) => (
              <div
                key={p.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '6px',
                  marginBottom: '6px',
                  padding: '6px 8px',
                  borderRadius: '6px',
                  backgroundColor: p.vencida ? '#fef2f2' : '#f0fdf4',
                  border: `1px solid ${p.vencida ? '#fecaca' : '#bbf7d0'}`
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, color: 'var(--gray-600)' }}>
                  <span style={{ fontSize: '14px' }}>
                    {p.vencida ? '\u26A0\uFE0F' : '\uD83D\uDCC5'}
                  </span>
                  <span style={{ fontWeight: '500' }}>
                    {p.tipo === 'vacuna' ? p.nombre : `Desp. ${p.nombre}`}
                  </span>
                  <span style={{ color: p.vencida ? '#dc2626' : 'var(--gray-400)' }}>
                    {new Date(p.fechaProgramada + 'T12:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                  {p.vencida && (
                    <span style={{
                      backgroundColor: '#fecaca',
                      color: '#991b1b',
                      fontSize: '9px',
                      padding: '1px 4px',
                      borderRadius: '4px',
                      fontWeight: '600'
                    }}>
                      Atrasada
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {!p.vencida && !isVetAccess && (() => {
                    const yaCreado = tieneRecordatorio(p);
                    const cargando = creandoRecordatorio[p.id];
                    return (
                      <button
                        className="btn btn-sm"
                        onClick={() => handleCrearRecordatorioProxima(p)}
                        disabled={yaCreado || cargando}
                        title={yaCreado ? 'Recordatorios ya creados' : 'Crear recordatorios (1 d\u00eda antes y 2 horas antes)'}
                        style={{
                          padding: '2px 6px',
                          fontSize: '12px',
                          backgroundColor: yaCreado ? '#dbeafe' : '#fff',
                          color: yaCreado ? '#1d4ed8' : '#6b7280',
                          border: `1px solid ${yaCreado ? '#93c5fd' : '#d1d5db'}`,
                          borderRadius: '4px',
                          cursor: yaCreado ? 'default' : 'pointer'
                        }}
                      >
                        {cargando
                          ? <span className="spinner-border spinner-border-sm" style={{ width: '12px', height: '12px' }}></span>
                          : <i className={`bi bi-bell${yaCreado ? '-fill' : ''}`}></i>
                        }
                      </button>
                    );
                  })()}
                  {p.estado === 'programada' && (
                    <>
                      <button
                        className="btn btn-sm"
                        onClick={() => {
                          setProximaParaConfirmar(p);
                          setShowConfirmarAplicacionModal(true);
                        }}
                        title="Confirmar aplicaci&oacute;n"
                        style={{
                          padding: '2px 8px',
                          fontSize: '10px',
                          backgroundColor: '#dcfce7',
                          color: '#166534',
                          border: '1px solid #bbf7d0',
                          borderRadius: '4px'
                        }}
                      >
                        <i className="bi bi-check2"></i> Aplicada
                      </button>
                      {!isVetAccess && (
                        <button
                          className="btn btn-sm"
                          onClick={() => handleDeleteProxima(p)}
                          title="Eliminar"
                          style={{
                            padding: '2px 6px',
                            fontSize: '10px',
                            backgroundColor: '#fef2f2',
                            color: '#dc2626',
                            border: '1px solid #fecaca',
                            borderRadius: '4px'
                          }}
                        >
                          <i className="bi bi-x"></i>
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-400)' }}>
            Sin vacunas o desparasitaciones programadas
          </div>
        )}
        </div>
        )}
      </div>

      {/* Modal de Agregar Próxima */}
      {showAddProximaModal && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 100000,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--space-4)'
          }}
          onClick={() => setShowAddProximaModal(false)}
        >
          <div
            className="card card-elevated"
            style={{ borderRadius: 'var(--radius-xl)', maxWidth: '450px', width: '100%', overflow: 'hidden' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header-gradient d-flex justify-content-between align-items-center" style={{ padding: 'var(--space-3) var(--space-4)' }}>
              <h5 className="mb-0" style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-bold)', color: 'var(--white)' }}>
                <i className="bi bi-calendar-plus me-2"></i>
                Programar pr&oacute;xima
              </h5>
              <button type="button" className="btn-close btn-close-white" onClick={() => setShowAddProximaModal(false)}></button>
            </div>
            <div className="card-body" style={{ padding: 'var(--space-4)' }}>
              <form onSubmit={(e) => {
                e.preventDefault();
                const fd = new FormData(e.target);
                handleAddProxima({
                  tipoProxima: fd.get('tipoProxima'),
                  nombre: fd.get('nombre'),
                  fechaProgramada: fd.get('fechaProgramada')
                });
              }}>
                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: 'var(--font-size-sm)', fontWeight: '500' }}>Tipo *</label>
                  <select className="form-select" name="tipoProxima" required style={{ fontSize: 'var(--font-size-sm)' }}>
                    <option value="vacuna">Vacuna</option>
                    <option value="desparasitacion">Desparasitaci&oacute;n</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: 'var(--font-size-sm)', fontWeight: '500' }}>Nombre / Producto *</label>
                  <input type="text" className="form-control" name="nombre" required placeholder="Ej: Rabia, Nexgard Spectra..." style={{ fontSize: 'var(--font-size-sm)' }} />
                </div>
                <div className="mb-4">
                  <label className="form-label" style={{ fontSize: 'var(--font-size-sm)', fontWeight: '500' }}>Fecha programada *</label>
                  <input type="date" className="form-control" name="fechaProgramada" required style={{ fontSize: 'var(--font-size-sm)' }} />
                </div>
                <div className="d-flex gap-2 justify-content-end">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowAddProximaModal(false)} style={{ fontSize: 'var(--font-size-sm)' }}>Cancelar</button>
                  <button type="submit" className="btn btn-primary" disabled={savingProxima} style={{ fontSize: 'var(--font-size-sm)' }}>
                    {savingProxima ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-calendar-plus me-2"></i>Programar</>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmar Aplicación */}
      {showConfirmarAplicacionModal && proximaParaConfirmar && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 100000,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--space-4)'
          }}
          onClick={() => { setShowConfirmarAplicacionModal(false); setProximaParaConfirmar(null); }}
        >
          <div
            className="card card-elevated"
            style={{ borderRadius: 'var(--radius-xl)', maxWidth: '450px', width: '100%', overflow: 'hidden' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header-gradient d-flex justify-content-between align-items-center" style={{ padding: 'var(--space-3) var(--space-4)' }}>
              <h5 className="mb-0" style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-bold)', color: 'var(--white)' }}>
                <i className="bi bi-check-circle me-2"></i>
                Confirmar aplicaci&oacute;n
              </h5>
              <button type="button" className="btn-close btn-close-white" onClick={() => { setShowConfirmarAplicacionModal(false); setProximaParaConfirmar(null); }}></button>
            </div>
            <div className="card-body" style={{ padding: 'var(--space-4)' }}>
              <div style={{ marginBottom: '16px', padding: '10px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                <div style={{ fontWeight: '600', fontSize: 'var(--font-size-sm)' }}>
                  {proximaParaConfirmar.tipo === 'vacuna' ? 'Vacuna' : 'Desparasitaci\u00f3n'}: {proximaParaConfirmar.nombre}
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>
                  Programada: {new Date(proximaParaConfirmar.fechaProgramada + 'T12:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}
                </div>
              </div>
              <form onSubmit={(e) => {
                e.preventDefault();
                const fd = new FormData(e.target);
                handleConfirmarAplicacion(proximaParaConfirmar, {
                  fecha: fd.get('fecha'),
                  laboratorio: fd.get('laboratorio') || null,
                  lote: fd.get('lote') || null,
                  aplicadoEn: fd.get('aplicadoEn') || null,
                });
              }}>
                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: 'var(--font-size-sm)', fontWeight: '500' }}>Fecha real de aplicaci&oacute;n *</label>
                  <input type="date" className="form-control" name="fecha" required defaultValue={new Date().toISOString().split('T')[0]} style={{ fontSize: 'var(--font-size-sm)' }} />
                </div>
                {proximaParaConfirmar.tipo === 'vacuna' && (
                  <div className="mb-3">
                    <label className="form-label" style={{ fontSize: 'var(--font-size-sm)', fontWeight: '500' }}>Laboratorio</label>
                    <input type="text" className="form-control" name="laboratorio" placeholder="Opcional" style={{ fontSize: 'var(--font-size-sm)' }} />
                  </div>
                )}
                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: 'var(--font-size-sm)', fontWeight: '500' }}>Lote</label>
                  <input type="text" className="form-control" name="lote" placeholder="Opcional" style={{ fontSize: 'var(--font-size-sm)' }} />
                </div>
                {proximaParaConfirmar.tipo === 'desparasitacion' && (
                  <div className="mb-3">
                    <label className="form-label" style={{ fontSize: 'var(--font-size-sm)', fontWeight: '500' }}>Aplicado en</label>
                    <select className="form-select" name="aplicadoEn" style={{ fontSize: 'var(--font-size-sm)' }}>
                      <option value="">Seleccionar...</option>
                      <option value="casa">Casa</option>
                      <option value="veterinaria">Veterinaria</option>
                      <option value="campana">Campa&ntilde;a</option>
                    </select>
                  </div>
                )}
                <div className="d-flex gap-2 justify-content-end">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => { setShowConfirmarAplicacionModal(false); setProximaParaConfirmar(null); }} style={{ fontSize: 'var(--font-size-sm)' }}>Cancelar</button>
                  <button type="submit" className="btn btn-primary" disabled={savingProxima} style={{ fontSize: 'var(--font-size-sm)' }}>
                    {savingProxima ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-check2 me-2"></i>Confirmar</>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
